import React, { useState, useEffect, useRef, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ActivityIndicator, FlatList, ScrollView } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase.config';
import { locationTracker } from '../services/locationTracking';
import { useRoute, useSettings, useBookings } from '../stores';
import { parseFirestoreTimestamp } from '../utils/formatting';

const functions = getFunctions(app);

const RoutePlanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mapRef = useRef(null);
  const fullscreenMapRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const fullscreenDirectionsRendererRef = useRef(null);
  const liveLocationMarkerRef = useRef(null);
  const fullscreenLiveLocationMarkerRef = useRef(null);
  const watchIdRef = useRef(null);

  // Get bookings from store
  const allStoreBookings = useBookings((state) => state.bookings);
  const [filterMode, setFilterMode] = useState("delivery_pickup"); // 'delivery_pickup', 'going_out', 'all'

  // Get selected rentals from navigation state (initial seed)
  const initialRentals = location.state?.rentals || [];
  const cachedRoute = location.state?.cachedRoute || null;
  // Get date from Base page, ensure it's properly normalized
  const selectedDateRaw = location.state?.selectedDate || new Date();
  const selectedDate = (() => {
    const date = new Date(selectedDateRaw);
    date.setHours(0, 0, 0, 0);
    return date;
  })();
  
  // Route store
  const getCachedRoute = useRoute((state) => state.getRoute);
  const setCachedRoute = useRoute((state) => state.setRoute);
  const isRouteValid = useRoute((state) => state.isRouteValid);
  
  // Helper: Get TZ day key for comparison
  const getTZDayKey = (ms) => {
    try {
      if (ms == null) return null;
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(new Date(ms));
      const y = parts.find((p) => p.type === "year")?.value;
      const m = parts.find((p) => p.type === "month")?.value;
      const d = parts.find((p) => p.type === "day")?.value;
      return y && m && d ? `${y}-${m}-${d}` : null;
    } catch (e) {
      return null;
    }
  };
  
  // Helper: Generate hash from bookings for route cache validation
  // eslint-disable-next-line no-unused-vars
  const generateBookingsHash = (bookings, startAddr) => {
    if (!bookings || bookings.length === 0) return "";
    const bookingsStr = bookings
      .map((b) => `${b.id || b.bookingId}:${b.deliveryAddress || ""}`)
      .sort()
      .join("|");
    
    const hashString = `${bookingsStr}|${startAddr || "GPS"}`;

    let hash = 0;
    for (let i = 0; i < hashString.length; i++) {
      const char = hashString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString();
  };

  // Compute rentals based on filter mode
  const rentals = useMemo(() => {
    // Helper to check dates
    const checkDate = (timestamp, targetDate) => {
      const date = parseFirestoreTimestamp(timestamp);
      if (!date) return false;
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d.getTime() === targetDate.getTime();
    };

    // Filter bookings for the selected date
    const dailyBookings = allStoreBookings.filter((b) => {
      const startMatches = checkDate(b.startDate, selectedDate);
      const endMatches = checkDate(b.endDate, selectedDate);
      // For route planner, we care about actions on this day (start or end)
      return startMatches || endMatches;
    });

    let filtered = [];

    if (filterMode === 'delivery_pickup') {
      // Replicate Base.js logic: Delivery/HomePickup AND Has Address
      filtered = dailyBookings.filter(r => {
        const hasDelivery = r.delivery || false;
        const hasHomePickup = r.homePickup || false;
        // Only include if it has a delivery action TODAY or pickup action TODAY
        const isDeliveryToday = hasDelivery && checkDate(r.startDate, selectedDate);
        const isPickupToday = hasHomePickup && checkDate(r.endDate, selectedDate);
        
        return (isDeliveryToday || isPickupToday) && r.deliveryAddress;
      });
    } else if (filterMode === 'going_out') {
      // Deliveries (Start Today) OR In-store Pickups (Start Today)
      filtered = dailyBookings.filter(r => {
        const isDelivery = r.delivery || false;
        const isStartToday = checkDate(r.startDate, selectedDate);
        
        // Delivery going out
        if (isDelivery && isStartToday) return true;
        
        // In-store pickup (Not delivery, Start Today)
        if (!isDelivery && isStartToday) return true;
        
        return false;
      });
    } else if (filterMode === 'all') {
      // All actions for today
      filtered = dailyBookings;
    }

    // Assign colors and sort
    const processed = filtered.map(r => {
      const isDelivery = r.delivery || false;
      const isHomePickup = r.homePickup || false;
      const isInStorePickup = r.inStorePickup || false;
      const isStartToday = checkDate(r.startDate, selectedDate);
      const isEndToday = checkDate(r.endDate, selectedDate);

      let color = '#B4761B'; // Default Yellow
      let borderColor = '#B4761B';
      let label = "In-Store Pickup";
      let sortOrder = 3;

      if (isDelivery && isStartToday) {
        sortOrder = 1;
        if (isInStorePickup) {
            color = '#C4453D'; // Red
            borderColor = '#8A9794'; // Gray
            label = "In-Store Dropoff";
        } else {
            color = '#C4453D';
            borderColor = '#C4453D';
            label = "Delivery";
        }
      } else if (isHomePickup && isEndToday) {
        sortOrder = 2;
        if (isInStorePickup) {
            color = '#1F8A5B'; // Green
            borderColor = '#1F8A5B';
            label = "In-Store Pickup";
        } else {
            color = '#1F8A5B';
            borderColor = '#1F8A5B';
            label = "Pickup";
        }
      } else if (isStartToday || isEndToday) {
          // In-Store
          color = '#B4761B';
          borderColor = '#B4761B';
          label = isStartToday ? "In-Store Pickup" : "In-Store Return";
      }

      return {
        ...r,
        fullName: r.fullName || `${r.firstName || ""} ${r.lastName || ""}`.trim() || "Unknown",
        cardColor: color,
        cardBorderColor: borderColor,
        cardLabel: label,
        sortOrder: sortOrder
      };
    });

    // Sort: Deliveries -> Pickups -> In-Store
    return processed.sort((a, b) => a.sortOrder - b.sortOrder);
  }, [allStoreBookings, selectedDate, filterMode]);

  const [currentLocation, setCurrentLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false); // Re-added calculating state
  const [route, setRoute] = useState(null);
  const [directions, setDirections] = useState([]);
  const [error, setError] = useState("");
  const [mapLoaded, setMapLoaded] = useState(false);
  const [startingAddress, setStartingAddress] = useState("");
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  // Journey tracking state
  const [isJourneyActive, setIsJourneyActive] = useState(false);
  const [currentETA, setCurrentETA] = useState(null);
  const [notificationsSent, setNotificationsSent] = useState([]);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Directions visibility state
  const [showDirections, setShowDirections] = useState(false);

  // Desktop detection
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load starting address from settings
  // Get settings from Zustand store
  const settings = useSettings((state) => state.settings);
  
  // Update starting address when settings change
  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      const address = settings.startingAddress?.address || "";
      console.log("🔍 [Debug] Settings loaded - startingAddress:", address);
      setStartingAddress(address);
    }
  }, [settings]);

  // Load Google Maps script
  useEffect(() => {
    console.log("🔍 [Debug] Checking Google Maps script status...");
    const GOOGLE_MAPS_API_KEY = "AIzaSyDnMrIVkx-6V0Brm4pxezeOVcTOELsTmdQ"; // Your Firebase API key

    // Check if script already loaded
    if (window.google && window.google.maps) {
      setMapLoaded(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(
      `script[src*="maps.googleapis.com"]`
    );
    if (existingScript) {
      console.log("⏳ Google Maps script already loading, waiting...");
      const checkLoaded = setInterval(() => {
        if (window.google && window.google.maps) {
          console.log("✅ Google Maps loaded from existing script");
          setMapLoaded(true);
          clearInterval(checkLoaded);
        }
      }, 100);
      setTimeout(() => {
        clearInterval(checkLoaded);
        if (!window.google || !window.google.maps) {
          console.error("❌ Google Maps script timeout");
          setError("Maps failed to load (timeout)");
        }
      }, 10000);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log("✅ Google Maps API loaded");
      setMapLoaded(true);
    };
    script.onerror = () => {
      console.error("❌ Failed to load Google Maps API");
      setError(
        "Failed to load Google Maps. Please check your internet connection."
      );
      setMapLoaded(false);
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Get current GPS location
  useEffect(() => {
    if (!mapLoaded) return;

    console.log("📍 Getting current GPS location...");

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          console.log("✅ GPS location:", JSON.stringify(coords, null, 2));
          setCurrentLocation(coords);
          setLoading(false);

          // Initialize map
          initializeMap(coords);
        },
        (error) => {
          console.warn("⚠️ GPS error:", JSON.stringify({
            code: error.code,
            message: error.message,
            PERMISSION_DENIED: error.PERMISSION_DENIED,
            POSITION_UNAVAILABLE: error.POSITION_UNAVAILABLE,
            TIMEOUT: error.TIMEOUT
          }, null, 2));
          // Default to Bonita Springs center
          const defaultCoords = { lat: 26.3398, lng: -81.7787 };
          setCurrentLocation(defaultCoords);
          setLoading(false);
          setError("Could not get GPS location. Using default location.");

          // Initialize map
          initializeMap(defaultCoords);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } else {
      console.error("❌ Geolocation not supported");
      const defaultCoords = { lat: 26.3398, lng: -81.7787 };
      setCurrentLocation(defaultCoords);
      setLoading(false);
      setError("Geolocation not supported by your browser.");
      initializeMap(defaultCoords);
    }
  }, [mapLoaded]);

  const initializeMap = (center) => {
    if (!window.google || !mapRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: center,
      zoom: 12,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }],
        },
        {
          featureType: "poi.business",
          stylers: [{ visibility: "off" }],
        },
        {
          featureType: "transit",
          elementType: "labels.icon",
          stylers: [{ visibility: "off" }],
        },
      ],
    });

    // Store map instance
    mapRef.current.mapInstance = map;

    // Add marker for starting location
    new window.google.maps.Marker({
      position: center,
      map: map,
      title: "Starting Location",
      icon: {
        url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
      },
    });

    // Add home base marker
    const homeGeocoder = new window.google.maps.Geocoder();
    homeGeocoder.geocode(
      { address: "9102 Bonita Beach Rd SE, Bonita Springs, FL 34135" },
      (results, status) => {
        if (status === "OK") {
          new window.google.maps.Marker({
            position: results[0].geometry.location,
            map: map,
            title: "Bonita Bikes - Home Base",
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: "#1F8A5B",
              fillOpacity: 1,
              strokeColor: "#FFFFFF",
              strokeWeight: 2,
            },
            label: {
              text: "🏠",
              fontSize: "18px",
            },
          });
        }
      }
    );

    // Add markers for customer addresses
    rentals.forEach((rental, index) => {
      if (rental.deliveryAddress) {
        // Geocode address and add marker
        geocodeAddress(rental.deliveryAddress, map, index + 1, rental.fullName);
      }
    });

    console.log("✅ Map initialized");

    // Start tracking user's live location with blue marker
    if (navigator.geolocation) {
      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const liveLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          // Create or update blue live tracking marker
          if (!liveLocationMarkerRef.current) {
            console.log("✅ Initial live location update:", JSON.stringify({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              accuracy: position.coords.accuracy
            }, null, 2));

            liveLocationMarkerRef.current = new window.google.maps.Marker({
              position: liveLocation,
              map: map,
              title: "Your Current Location (Live)",
              icon: {
                url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
              },
              zIndex: 1000, // Keep on top
            });
          } else {
             // Only log significant changes to avoid spam
             const currentPos = liveLocationMarkerRef.current.getPosition();
             const dist = Math.sqrt(
               Math.pow(currentPos.lat() - liveLocation.lat, 2) + 
               Math.pow(currentPos.lng() - liveLocation.lng, 2)
             );
             if (dist > 0.0001) { // approx 10m
               console.log("📍 Live location updated:", JSON.stringify({
                 lat: liveLocation.lat,
                 lng: liveLocation.lng,
                 accuracy: position.coords.accuracy
               }, null, 2));
             }

            // Update existing marker position
            liveLocationMarkerRef.current.setPosition(liveLocation);
          }
        },
        (error) => {
          console.warn("⚠️ Live tracking error:", JSON.stringify({
            code: error.code,
            message: error.message
          }, null, 2));
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 5000,
        }
      );
    }
  };

  const geocodeAddress = (address, map, number, customerName) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: address }, (results, status) => {
      if (status === "OK") {
        new window.google.maps.Marker({
          position: results[0].geometry.location,
          map: map,
          label: number.toString(),
          title: `${number}. ${customerName}: ${address}`,
        });
      } else {
        console.warn(`⚠️ Geocode failed for ${address}:`, status);
      }
    });
  };

  const startNavigation = () => {
    if (!route) {
      alert("Please calculate route first");
      return;
    }

    // Get first destination
    const firstDest = rentals[0];
    if (!firstDest || !firstDest.deliveryAddress) {
      alert("No destination address found");
      return;
    }

    // Open Google Maps app/website with navigation
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${
      currentLocation.lat
    },${currentLocation.lng}&destination=${encodeURIComponent(
      firstDest.deliveryAddress
    )}&travelmode=driving`;

    window.open(mapsUrl, "_blank");
  };

  // Start journey tracking
  const startJourney = () => {
    if (!route) {
      alert("Please wait for route to load from dashboard");
      return;
    }

    console.log("🚗 Starting journey tracking...");
    setIsJourneyActive(true);
    setNotificationsSent([]);
    setCurrentETA(null);

    // Prepare destinations for tracker
    const destinations = rentals.map((rental, index) => ({
      id: rental.id,
      name: rental.fullName,
      phone: rental.customerPhone || rental.phone,
      address: rental.deliveryAddress,
      lat: null, // Will be geocoded by tracker
      lng: null,
      index: index,
    }));

    // Set up notification callback
    locationTracker.setNotificationCallback(async (destination, etaMinutes) => {
      console.log(`🔔 Notification triggered for ${destination.name}`);

      try {
        // Call Cloud Function to send SMS
        const sendArrivalNotification = httpsCallable(
          functions,
          "sendArrivalNotification"
        );
        const result = await sendArrivalNotification({
          customerPhone: destination.phone,
          customerName: destination.name,
          etaMinutes: etaMinutes,
        });

        console.log("✅ Arrival notification sent:", result.data);

        // Track that we sent notification
        setNotificationsSent((prev) => [...prev, destination.id]);
      } catch (error) {
        console.error("❌ Failed to send arrival notification:", error);
        alert(
          `Failed to send notification to ${destination.name}: ${error.message}`
        );
      }
    });

    // Start tracking
    locationTracker.startTracking(
      destinations,
      (location) => {
        // Location update callback
        console.log("📍 Location updated:", location);
      },
      (etaData) => {
        // ETA update callback
        console.log("⏱️ ETA update:", etaData);
        setCurrentETA(etaData);
      }
    );
  };

  // Stop journey tracking
  const stopJourney = () => {
    console.log("🛑 Stopping journey tracking");
    locationTracker.stopTracking();
    setIsJourneyActive(false);
    setCurrentETA(null);
  };

  // Calculate route fresh (Debug Mode)
  const calculateRouteFresh = async () => {
    if (!window.google || !mapRef.current.mapInstance) {
      console.log("⏳ Map not ready for calculation");
      return;
    }

    setCalculating(true);
    setError("");
    console.log("🔄 Calculating FRESH route (Debug Mode)...");

    try {
      // Get start point - prioritize settings, then GPS
      let startPoint;
      
      if (startingAddress && startingAddress.trim()) {
        console.log("📍 Using starting address from settings:", startingAddress);
        const geocoder = new window.google.maps.Geocoder();
        try {
          const result = await new Promise((resolve, reject) => {
            geocoder.geocode({ address: startingAddress }, (results, status) => {
              if (status === "OK") resolve(results[0].geometry.location);
              else reject(new Error(status));
            });
          });
          startPoint = result;
        } catch (e) {
          console.warn("⚠️ Failed to geocode starting address, using GPS fallback");
          startPoint = new window.google.maps.LatLng(currentLocation.lat, currentLocation.lng);
        }
      } else {
        console.log("📍 Using GPS start point");
        startPoint = new window.google.maps.LatLng(currentLocation.lat, currentLocation.lng);
      }

      // Prepare waypoints
      const waypoints = rentals
        .filter((r) => r.deliveryAddress)
        .map((r) => ({
          location: r.deliveryAddress,
          stopover: true,
        }));

      if (waypoints.length === 0) {
        setError("No addresses found for selected customers");
        setCalculating(false);
        return;
      }

      const directionsService = new window.google.maps.DirectionsService();
      const request = {
        origin: startPoint,
        destination: waypoints[waypoints.length - 1].location,
        waypoints: waypoints.slice(0, -1),
        optimizeWaypoints: true,
        travelMode: window.google.maps.TravelMode.DRIVING,
      };

      directionsService.route(request, (result, status) => {
        if (status === "OK") {
          console.log("✅ Route calculated successfully");
          setRoute(result);

          const steps = [];
          result.routes[0].legs.forEach((leg, legIndex) => {
            leg.steps.forEach((step, stepIndex) => {
              steps.push({
                id: `${legIndex}-${stepIndex}`,
                instructions: step.instructions.replace(/<[^>]*>/g, ""),
                distance: step.distance.text,
                duration: step.duration.text,
              });
            });
          });
          setDirections(steps);

          if (!directionsRendererRef.current) {
            directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
              map: mapRef.current.mapInstance,
              suppressMarkers: false,
            });
          }
          directionsRendererRef.current.setDirections(result);
        } else {
          console.error("❌ Directions request failed:", status);
          setError(`Failed to calculate route: ${status}`);
        }
        setCalculating(false);
      });
    } catch (error) {
      console.error("❌ Route calculation error:", error);
      setError(error.message);
      setCalculating(false);
    }
  };

  // Auto-load route - DEBUG MODE: Always calculate fresh
  useEffect(() => {
    if (
      mapLoaded &&
      currentLocation &&
      !loading &&
      !route &&
      !calculating && // Prevent multiple calls
      rentals.length > 0
    ) {
      // DEBUG MODE: Commented out Zustand store check
      /*
      // Check Zustand store FIRST (Base.js calculates routes in background)
      const dayKey = getTZDayKey(selectedDate.getTime());
      const bookingsHash = generateBookingsHash(rentals, startingAddress);
      
      if (isRouteValid(dayKey, bookingsHash)) {
        const cached = getCachedRoute(dayKey);
        if (cached && cached.route) {
          console.log("✅ Using cached route from Zustand store for", dayKey);
          setRoute(cached.route);
          // ... (rest of cached route logic)
          return;
        }
      }
      */
      
      console.log("🔧 Debug Mode: Skipping cache, forcing fresh calculation");
      calculateRouteFresh();
    }
  }, [mapLoaded, currentLocation, loading, selectedDate, rentals, startingAddress]);

  // Initialize fullscreen map when entering fullscreen
  useEffect(() => {
    if (
      isFullscreen &&
      fullscreenMapRef.current &&
      mapLoaded &&
      currentLocation
    ) {
      console.log("🗺️ Initializing fullscreen map...");

      const map = new window.google.maps.Map(fullscreenMapRef.current, {
        center: currentLocation,
        zoom: 12,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: false,
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "poi.business",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "transit",
            elementType: "labels.icon",
            stylers: [{ visibility: "off" }],
          },
        ],
      });

      // Add home base marker
      const homeGeocoder = new window.google.maps.Geocoder();
      homeGeocoder.geocode(
        { address: "9102 Bonita Beach Rd SE, Bonita Springs, FL 34135" },
        (results, status) => {
          if (status === "OK") {
            new window.google.maps.Marker({
              position: results[0].geometry.location,
              map: map,
              title: "Bonita Bikes - Home Base",
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 10,
                fillColor: "#1F8A5B",
                fillOpacity: 1,
                strokeColor: "#FFFFFF",
                strokeWeight: 2,
              },
              label: {
                text: "🏠",
                fontSize: "18px",
              },
            });
          }
        }
      );

      // Add customer markers
      rentals.forEach((rental, index) => {
        if (rental.deliveryAddress) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode(
            { address: rental.deliveryAddress },
            (results, status) => {
              if (status === "OK") {
                new window.google.maps.Marker({
                  position: results[0].geometry.location,
                  map: map,
                  label: (index + 1).toString(),
                  title: `${index + 1}. ${rental.fullName}: ${
                    rental.deliveryAddress
                  }`,
                });
              }
            }
          );
        }
      });

      // Show route if it exists
      if (route) {
        fullscreenDirectionsRendererRef.current =
          new window.google.maps.DirectionsRenderer({
            map: map,
            suppressMarkers: false,
          });
        fullscreenDirectionsRendererRef.current.setDirections(route);
      }

      // Add live location tracking
      if (navigator.geolocation) {
        const watchId = navigator.geolocation.watchPosition(
          (position) => {
            const liveLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };

            if (!fullscreenLiveLocationMarkerRef.current) {
              fullscreenLiveLocationMarkerRef.current =
                new window.google.maps.Marker({
                  position: liveLocation,
                  map: map,
                  title: "Your Current Location (Live)",
                  icon: {
                    url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                  },
                  zIndex: 1000,
                });
            } else {
              fullscreenLiveLocationMarkerRef.current.setPosition(liveLocation);
            }
          },
          (error) => console.warn("⚠️ Live tracking error:", error.message),
          {
            enableHighAccuracy: true,
            maximumAge: 0,
            timeout: 5000,
          }
        );

        return () => {
          navigator.geolocation.clearWatch(watchId);
          if (fullscreenLiveLocationMarkerRef.current) {
            fullscreenLiveLocationMarkerRef.current = null;
          }
        };
      }
    }
  }, [isFullscreen, mapLoaded, currentLocation, route]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isJourneyActive) {
        locationTracker.stopTracking();
      }
      // Stop watching live location
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        console.log("🛑 Stopped live location tracking");
      }
    };
  }, [isJourneyActive]);

  if (rentals.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>No customers selected</Text>
        <TouchableOpacity dataSet={{ hover: "lift" }}
          style={styles.backButton}
          onPress={() => navigate(-1)}
        >
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Back Button - Top Left */}
      <TouchableOpacity dataSet={{ hover: "lift" }}
        style={styles.topBackButton}
        onPress={() => navigate(-1)}
      >
        <Text style={styles.topBackButtonText}>←</Text>
      </TouchableOpacity>

      {/* Fullscreen Map Overlay */}
      {isFullscreen && (
        <View style={styles.fullscreenOverlay}>
          <div
            ref={fullscreenMapRef}
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />

          {/* Fullscreen Controls */}
          <View style={styles.fullscreenControls}>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.minimizeButton}
              onPress={() => setIsFullscreen(false)}
            >
              <Text style={styles.minimizeButtonIcon}>↩️</Text>
            </TouchableOpacity>

            {!isJourneyActive ? (
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={styles.startButton}
                onPress={startJourney}
              >
                <Text style={styles.startButtonIcon}>🚗</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.stopButton} onPress={stopJourney}>
                <Text style={styles.stopButtonIcon}>⏸</Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Journey Status (if active) */}
          {isJourneyActive && currentETA && (
            <View style={styles.fullscreenStatus}>
              <Text style={styles.fullscreenStatusText}>
                Next: {currentETA.destination.name}
              </Text>
              <Text style={styles.fullscreenETA}>
                ETA: {currentETA.durationMinutes} min
              </Text>
            </View>
          )}
        </View>
      )}

      <View style={isDesktop ? styles.desktopWrapper : null}>
        {/* Map Container - Takes 3/4 of vertical space */}
        <View style={styles.mapSection}>
          <div
            ref={mapRef}
            style={{ width: "100%", height: "100%", borderRadius: 12 }}
          />
        </View>

        <ScrollView
          style={styles.scrollContent}
          contentContainerStyle={styles.scrollContentContainer}
        >
          {/* Error Message */}
          {error && (
            <View style={styles.errorBanner}>
              <Text style={styles.errorBannerText}>{error}</Text>
            </View>
          )}

        {/* Map Action Buttons */}
        <View style={styles.mapActionButtons}>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[
              styles.startJourneyButtonCompact,
              !route && styles.startJourneyButtonCompactDisabled,
              isJourneyActive && styles.stopJourneyButtonCompact,
            ]}
            onPress={isJourneyActive ? stopJourney : startJourney}
            disabled={!route}
          >
            <Text style={styles.startJourneyButtonCompactText}>
              {isJourneyActive ? "🛑 Stop" : "🚗 Start"}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[
              styles.fullscreenToggleButton,
              !route && styles.fullscreenToggleButtonDisabled,
            ]}
            onPress={() => setIsFullscreen(true)}
            disabled={!route}
          >
            <Text style={styles.fullscreenToggleButtonText}>⛶ Full Screen</Text>
          </TouchableOpacity>
        </View>

        {/* Filter Toggle */}
        <View style={styles.toggleContainer}>
          <TouchableOpacity dataSet={{ hover: "lift" }} 
            style={[styles.filterButton, filterMode === 'delivery_pickup' && styles.filterButtonActive]}
            onPress={() => setFilterMode('delivery_pickup')}
          >
            <Text style={[styles.filterButtonText, filterMode === 'delivery_pickup' && styles.filterButtonTextActive]}>Delivery/Pickup</Text>
          </TouchableOpacity>
          <TouchableOpacity dataSet={{ hover: "lift" }} 
            style={[styles.filterButton, filterMode === 'going_out' && styles.filterButtonActive]}
            onPress={() => setFilterMode('going_out')}
          >
            <Text style={[styles.filterButtonText, filterMode === 'going_out' && styles.filterButtonTextActive]}>Going Out</Text>
          </TouchableOpacity>
          <TouchableOpacity dataSet={{ hover: "lift" }} 
            style={[styles.filterButton, filterMode === 'all' && styles.filterButtonActive]}
            onPress={() => setFilterMode('all')}
          >
            <Text style={[styles.filterButtonText, filterMode === 'all' && styles.filterButtonTextActive]}>All</Text>
          </TouchableOpacity>
        </View>

        {/* Route Info & Directions */}
        {route && (
          <View style={styles.routeInfo}>
            <View style={styles.routeStats}>
              <View style={styles.statItem}>
                <Text style={styles.statLabel}>Total Distance</Text>
                <Text style={styles.statValue}>
                  {route.routes[0].legs.reduce(
                    (sum, leg) => sum + leg.distance.value,
                    0
                  ) /
                    1609.34 <
                  1
                    ? `${(
                        route.routes[0].legs.reduce(
                          (sum, leg) => sum + leg.distance.value,
                          0
                        ) * 3.28084
                      ).toFixed(0)} ft`
                    : `${(
                        route.routes[0].legs.reduce(
                          (sum, leg) => sum + leg.distance.value,
                          0
                        ) / 1609.34
                      ).toFixed(1)} mi`}
                </Text>
              </View>
              <View style={styles.statItem}>
                <Text style={styles.statLabel}>Total Time</Text>
                <Text style={styles.statValue}>
                  {Math.round(
                    route.routes[0].legs.reduce(
                      (sum, leg) => sum + leg.duration.value,
                      0
                    ) / 60
                  )}{" "}
                  min
                </Text>
              </View>
              <View style={styles.statItem}>
                <Text style={styles.statLabel}>Stops</Text>
                <Text style={styles.statValue}>{rentals.length}</Text>
              </View>
            </View>

            {/* Journey Status Display */}
            {isJourneyActive && (
              <View style={styles.journeyStatus}>
                <Text style={styles.journeyStatusTitle}>🚗 Journey Active</Text>
                {currentETA && (
                  <Text style={styles.journeyStatusText}>
                    Next Stop: {currentETA.destination.name}
                  </Text>
                )}
                {currentETA && (
                  <Text style={styles.journeyStatusETA}>
                    ETA: {currentETA.durationMinutes} min (
                    {currentETA.distanceMiles} mi)
                  </Text>
                )}
                <Text style={styles.journeyStatusNotifications}>
                  Notifications Sent: {notificationsSent.length}/
                  {rentals.length}
                </Text>
              </View>
            )}

            {/* Turn-by-Turn Directions */}
            {showDirections && (
              <View style={styles.directionsContainer}>
                <Text style={styles.directionsTitle}>
                  Turn-by-Turn Directions
                </Text>
                <ScrollView style={styles.directionsList}>
                  {directions.map((step, index) => (
                    <View key={step.id} style={styles.directionItem}>
                      <Text style={styles.directionNumber}>{index + 1}</Text>
                      <View style={styles.directionContent}>
                        <Text style={styles.directionText}>
                          {step.instructions}
                        </Text>
                        <Text style={styles.directionDistance}>
                          {step.distance} • {step.duration}
                        </Text>
                      </View>
                    </View>
                  ))}
                </ScrollView>
              </View>
            )}
          </View>
        )}

        {/* Toggle Directions Button */}
        {route && (
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={styles.toggleDirectionsButton}
            onPress={() => setShowDirections(!showDirections)}
          >
            <Text style={styles.toggleDirectionsButtonText}>
              {showDirections ? "▼ Hide Directions" : "▶ Show Directions"}
            </Text>
          </TouchableOpacity>
        )}

          {/* Selected Customers */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              Destinations ({rentals.length})
            </Text>

            <FlatList
              data={rentals}
              keyExtractor={(item) => item.id}
              renderItem={({ item, index }) => (
                <View style={[
                    styles.destinationItemWrapper, 
                    { 
                        borderLeftColor: item.cardBorderColor || item.cardColor || '#DDE3E2', 
                        borderLeftWidth: 6, 
                        borderWidth: 2,
                        borderColor: item.cardBorderColor || item.cardColor || '#DDE3E2',
                        paddingLeft: 12, 
                        backgroundColor: '#F4F6F6', 
                        marginBottom: 8, 
                        borderRadius: 6 
                    }
                ]}>
                  <View style={styles.destinationItem}>
                    <Text style={[styles.destinationNumber, { backgroundColor: item.cardColor || '#0E7C6B' }]}>{index + 1}</Text>
                    <View style={styles.destinationInfo}>
                      <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap'}}>
                          <Text style={styles.destinationName}>{item.fullName}</Text>
                          {item.cardLabel && (
                              <View style={{marginLeft: 8, paddingHorizontal: 6, paddingVertical: 2, backgroundColor: item.cardColor, borderRadius: 4}}>
                                  <Text style={{fontSize: 10, color: '#FFFFFF', fontWeight: 'bold'}}>{item.cardLabel}</Text>
                              </View>
                          )}
                      </View>
                      <Text style={styles.destinationAddress}>
                        {item.deliveryAddress
                          ? item.deliveryAddress.replace(/\*/g, "")
                          : "No address (In-Store)"}
                      </Text>
                    </View>
                  </View>
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.messageIconButton, { backgroundColor: item.cardColor || '#0E7C6B' }]}
                    onPress={() => {
                      console.log("💬 Opening messages for:", item.fullName);
                      navigate("/messages", {
                        state: { customer: item },
                      });
                    }}
                  >
                    <Text style={styles.messageIconButtonText}>💬</Text>
                  </TouchableOpacity>
                </View>
              )}
              style={styles.destinationList}
            />
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  desktopWrapper: {
    width: "100%",
    maxWidth: 800,
    alignSelf: "center",
  },
  scrollContent: {
    flex: 1,
  },
  scrollContentContainer: {
    paddingBottom: 80,
  },
  bottomNav: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#FFFFFF",
    paddingVertical: 15,
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: "#DDE3E2",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  fullscreenButton: {
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  fullscreenButtonText: {
    fontSize: 24,
    color: "#0E7C6B",
    fontWeight: "bold",
  },
  fullscreenButtonDisabled: {
    color: "#AEB8B6",
  },
  topBackButton: {
    position: "absolute",
    top: 16,
    left: 16,
    zIndex: 1000,
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#0E7C6B",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  topBackButtonText: {
    color: "#FFFFFF",
    fontSize: 24,
    fontWeight: "bold",
  },
  backButton: {
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  backButtonText: {
    fontSize: 24,
    color: "#0E7C6B",
    fontWeight: "bold",
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  section: {
    backgroundColor: "#FFFFFF",
    marginHorizontal: 16,
    marginTop: 16,
    padding: 12,
    borderRadius: 6,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 8,
  },
  locationToggle: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 8,
  },
  toggleButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#0E7C6B",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
  },
  toggleButtonActive: {
    backgroundColor: "#0E7C6B",
  },
  toggleButtonText: {
    fontSize: 13,
    color: "#0E7C6B",
    fontWeight: "600",
  },
  toggleButtonTextActive: {
    color: "#FFFFFF",
  },
  input: {
    height: 38,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 5,
    paddingHorizontal: 10,
    fontSize: 14,
    backgroundColor: "#F4F6F6",
    outlineStyle: "none",
  },
  currentLocationText: {
    fontSize: 13,
    color: "#5C6B68",
    padding: 10,
    backgroundColor: "#EDF1F0",
    borderRadius: 5,
  },
  destinationList: {
    maxHeight: 200,
  },
  destinationItemWrapper: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
    gap: 8,
  },
  destinationItem: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  messageIconButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#0E7C6B",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  messageIconButtonText: {
    fontSize: 20,
  },
  destinationNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#0E7C6B",
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
    lineHeight: 32,
    marginRight: 12,
  },
  destinationInfo: {
    flex: 1,
  },
  destinationName: {
    fontSize: 15,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 4,
  },
  destinationAddress: {
    fontSize: 13,
    color: "#5C6B68",
  },
  errorBanner: {
    backgroundColor: "#FDF3E3",
    padding: 12,
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  errorBannerText: {
    color: "#7A4E12",
    fontSize: 14,
  },
  calculateButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 10,
    marginHorizontal: 16,
    marginTop: 12,
    borderRadius: 5,
    alignItems: "center",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  calculateButtonDisabled: {
    backgroundColor: "#AEB8B6",
  },
  calculateButtonContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  buttonSpinner: {
    marginRight: 8,
  },
  calculateButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
  mapSection: {
    height: "75vh",
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  mapActionButtons: {
    flexDirection: "row",
    gap: 12,
    marginHorizontal: 16,
    marginTop: 12,
  },
  startJourneyButtonCompact: {
    flex: 1,
    backgroundColor: "#1F8A5B",
    paddingVertical: 10,
    borderRadius: 5,
    alignItems: "center",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  startJourneyButtonCompactDisabled: {
    backgroundColor: "#AEB8B6",
  },
  stopJourneyButtonCompact: {
    backgroundColor: "#C4453D",
  },
  startJourneyButtonCompactText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
  fullscreenToggleButton: {
    flex: 1,
    backgroundColor: "#0E7C6B",
    paddingVertical: 10,
    borderRadius: 5,
    alignItems: "center",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  fullscreenToggleButtonDisabled: {
    backgroundColor: "#AEB8B6",
  },
  fullscreenToggleButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
  toggleDirectionsButton: {
    backgroundColor: "#F4F6F6",
    paddingVertical: 8,
    marginHorizontal: 16,
    marginTop: 2,
    marginBottom: 2,
    borderRadius: 5,
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  toggleDirectionsButtonText: {
    color: "#2E3A38",
    fontSize: 14,
    fontWeight: "600",
  },
  routeInfo: {
    backgroundColor: "#FFFFFF",
    marginHorizontal: 16,
    marginTop: 16,
    marginBottom: 8,
    padding: 12,
    borderRadius: 8,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  routeStats: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginBottom: 0,
    paddingBottom: 0,
  },
  statItem: {
    alignItems: "center",
  },
  statLabel: {
    fontSize: 12,
    color: "#5C6B68",
    marginBottom: 4,
  },
  statValue: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#0E7C6B",
  },
  journeyControls: {
    marginBottom: 16,
    gap: 12,
  },
  startJourneyButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 16,
    borderRadius: 6,
    alignItems: "center",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  startJourneyButtonText: {
    color: "#FFFFFF",
    fontSize: 17,
    fontWeight: "bold",
  },
  stopJourneyButton: {
    backgroundColor: "#C4453D",
    paddingVertical: 14,
    borderRadius: 6,
    alignItems: "center",
  },
  stopJourneyButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  journeyStatus: {
    backgroundColor: "#E4F4EC",
    padding: 16,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#1F8A5B",
    marginTop: 10,
  },
  journeyStatusTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#1F8A5B",
    marginBottom: 8,
  },
  journeyStatusText: {
    fontSize: 15,
    color: "#2E3A38",
    marginBottom: 4,
  },
  journeyStatusETA: {
    fontSize: 15,
    color: "#5C6B68",
    marginBottom: 4,
  },
  journeyStatusNotifications: {
    fontSize: 14,
    color: "#0E7C6B",
    marginTop: 8,
    fontWeight: "600",
  },
  navigationButton: {
    backgroundColor: "#1F8A5B",
    paddingVertical: 14,
    borderRadius: 6,
    alignItems: "center",
  },
  navigationButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  directionsContainer: {
    marginTop: 8,
  },
  directionsTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 12,
  },
  directionsList: {
    maxHeight: 300,
  },
  directionItem: {
    flexDirection: "row",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  directionNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#E3F2EF",
    color: "#0E7C6B",
    fontSize: 14,
    fontWeight: "bold",
    textAlign: "center",
    lineHeight: 28,
    marginRight: 12,
  },
  directionContent: {
    flex: 1,
  },
  directionText: {
    fontSize: 14,
    color: "#2E3A38",
    marginBottom: 4,
  },
  directionDistance: {
    fontSize: 12,
    color: "#5C6B68",
  },
  errorText: {
    fontSize: 18,
    color: "#C4453D",
    textAlign: "center",
    marginTop: 40,
  },
  fullscreenOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    backgroundColor: "#0F1A19",
  },
  fullscreenControls: {
    position: "absolute",
    bottom: 30,
    left: 0,
    right: 0,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 20,
    zIndex: 1001,
  },
  minimizeButton: {
    width: 75,
    height: 75,
    borderRadius: 37.5,
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 8,
  },
  minimizeButtonIcon: {
    color: "#2E3A38",
    fontSize: 36,
    fontWeight: "bold",
  },
  startButton: {
    width: 75,
    height: 75,
    borderRadius: 37.5,
    backgroundColor: "rgba(0, 122, 255, 0.5)",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 8,
  },
  startButtonIcon: {
    color: "#FFFFFF",
    fontSize: 40,
    fontWeight: "bold",
  },
  stopButton: {
    width: 75,
    height: 75,
    borderRadius: 37.5,
    backgroundColor: "rgba(255, 59, 48, 0.5)",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 8,
  },
  stopButtonIcon: {
    color: "#FFFFFF",
    fontSize: 40,
    fontWeight: "bold",
  },
  fullscreenStatus: {
    position: "absolute",
    top: 20,
    left: 16,
    right: 16,
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    padding: 16,
    borderRadius: 8,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 5,
    zIndex: 1001,
  },
  fullscreenStatusText: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 4,
  },
  fullscreenETA: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#0E7C6B",
  },
  toggleContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginVertical: 12,
    backgroundColor: '#EDF1F0',
    borderRadius: 6,
    padding: 3,
    marginHorizontal: 16,
  },
  filterButton: {
    flex: 1,
    paddingVertical: 8,
    alignItems: 'center',
    borderRadius: 5,
  },
  filterButtonActive: {
    backgroundColor: '#FFFFFF',
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 1,
  },
  filterButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#5C6B68',
  },
  filterButtonTextActive: {
    color: '#0E7C6B',
  },
});

export default RoutePlanner;

